import React, { useMemo, useState } from 'react';
import { Button, Card, DatePicker, Drawer, Form, Select, Space, Table, Tooltip, Typography, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../../config/api';
import { DownloadOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import { RetainIQCallLogDetail, type RetainIQLogDetail } from './RetainIQCallLogDetail';
import './retainiq.css';

const { RangePicker } = DatePicker;

type LogRow = RetainIQLogDetail & {
  created_by_name?: string;
};

const ReportCellText: React.FC<{ value?: string | null }> = ({ value }) => {
  const text = value?.trim() ? value.trim() : '—';
  if (text === '—') {
    return (
      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
        —
      </Typography.Text>
    );
  }
  return (
    <Tooltip title={text} placement="topLeft">
      <Typography.Text ellipsis style={{ display: 'block', maxWidth: '100%', fontSize: 13 }}>
        {text}
      </Typography.Text>
    </Tooltip>
  );
};

export const RetainIQReports: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [selectedLog, setSelectedLog] = useState<LogRow | null>(null);
  const [params, setParams] = useState<Record<string, string>>(() => ({
    page: '1',
    limit: '50',
    created_from: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
    created_to: dayjs().format('YYYY-MM-DD'),
  }));

  const { data, isLoading, refetch } = useQuery(
    ['retain-iq-reports', params],
    async () => {
      const res = await api.get('/api/retain-iq/reports', { params });
      return res.data as { logs: LogRow[]; total: number; page: number; limit: number };
    },
    { keepPreviousData: true }
  );

  const { data: agentsData } = useQuery('retain-iq-agents-reports', () =>
    api.get('/api/retain-iq/agents').then((r) => r.data)
  );
  const agents = agentsData?.agents || [];

  const openTicket = (n: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    navigate(`/workspace/number/${n}`);
  };

  const columns: ColumnsType<LogRow> = useMemo(
    () => [
      {
        title: 'Date',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 165,
        render: (t: string) => dayjs(t).format('YYYY-MM-DD HH:mm'),
      },
      {
        title: 'Business',
        dataIndex: 'business_name',
        key: 'business_name',
        ellipsis: { showTitle: false },
        render: (name: string) => <ReportCellText value={name} />,
      },
      {
        title: 'Agent',
        dataIndex: 'agent_name',
        key: 'agent_name',
        width: 130,
        ellipsis: { showTitle: false },
        render: (name: string) => <ReportCellText value={name} />,
      },
      {
        title: 'Call type',
        key: 'call_type',
        width: 150,
        ellipsis: { showTitle: false },
        render: (_: unknown, row) => <ReportCellText value={String(row.payload?.call_type || '')} />,
      },
      {
        title: 'Outcome',
        key: 'outcome',
        width: 130,
        ellipsis: { showTitle: false },
        render: (_: unknown, row) => <ReportCellText value={String(row.payload?.call_outcome || '')} />,
      },
      {
        title: 'Risk',
        key: 'risk',
        width: 72,
        render: (_: unknown, row) => <ReportCellText value={String(row.payload?.risk_level || '')} />,
      },
      {
        title: 'Follow-up',
        key: 'followup_date',
        width: 115,
        render: (_: unknown, row) => {
          const d = row.payload?.followup_date;
          return <ReportCellText value={d && String(d).trim() ? String(d) : ''} />;
        },
      },
      {
        title: 'Ticket',
        dataIndex: 'ticket_number',
        key: 'ticket_number',
        width: 95,
        render: (n: string | null) => {
          if (!n) return <Typography.Text type="secondary">—</Typography.Text>;
          return (
            <Tooltip title="Open ticket in workspace">
              <Button
                type="link"
                onClick={(e) => openTicket(n, e)}
                style={{ padding: 0, fontFamily: 'monospace', height: 'auto' }}
              >
                #{n}
              </Button>
            </Tooltip>
          );
        },
      },
      {
        title: 'Report',
        key: 'view',
        width: 88,
        fixed: 'right',
        align: 'center',
        render: (_: unknown, row) => (
          <Tooltip title="View full call log">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedLog(row);
              }}
              style={{ paddingInline: 4 }}
            >
              View
            </Button>
          </Tooltip>
        ),
      },
    ],
    [navigate]
  );

  const onSearch = (vals: { range?: [dayjs.Dayjs, dayjs.Dayjs]; agent_user_id?: string; call_type?: string; has_ticket?: string; q?: string }) => {
    setParams((prev) => {
      const next: Record<string, string> = { ...prev, page: '1' };
      delete next.created_from;
      delete next.created_to;
      delete next.agent_user_id;
      delete next.call_type;
      delete next.has_ticket;
      delete next.q;
      if (vals.range?.[0]) next.created_from = vals.range[0].format('YYYY-MM-DD');
      if (vals.range?.[1]) next.created_to = vals.range[1].format('YYYY-MM-DD');
      if (vals.agent_user_id) next.agent_user_id = vals.agent_user_id;
      if (vals.call_type) next.call_type = vals.call_type;
      if (vals.has_ticket) next.has_ticket = vals.has_ticket;
      if (vals.q) next.q = vals.q;
      return next;
    });
  };

  const exportCsv = async () => {
    const res = await api.get('/api/retain-iq/reports/export', {
      params: {
        created_from: params.created_from,
        created_to: params.created_to,
        business_id: params.business_id,
        agent_user_id: params.agent_user_id,
      },
      responseType: 'blob',
    });
    const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'retain_iq_report.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <Typography.Title level={4}>RetainIQ reports</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
        Click a row or the <EyeOutlined /> View action to open the full call log. Hover truncated cells to see the full text.
      </Typography.Paragraph>
      <Card style={{ marginBottom: 16 }}>
        <Form
          form={form}
          layout="inline"
          onFinish={onSearch}
          initialValues={{ range: [dayjs().subtract(30, 'day'), dayjs()] }}
        >
          <Form.Item name="range" label="Date range">
            <RangePicker />
          </Form.Item>
          <Form.Item name="agent_user_id" label="Agent">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Any"
              style={{ minWidth: 200 }}
              options={agents.map((a: { id: string; name: string }) => ({ value: a.id, label: a.name }))}
            />
          </Form.Item>
          <Form.Item name="call_type" label="Call type">
            <Input placeholder="Contains" allowClear style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="has_ticket" label="Ticket">
            <Select
              allowClear
              placeholder="Any"
              style={{ width: 120 }}
              options={[
                { value: 'true', label: 'Has ticket' },
                { value: 'false', label: 'No ticket' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="q"
            label="Search"
            tooltip="Matches if the text appears anywhere in the saved call log (all fields) or in the business name."
          >
            <Input placeholder="Text in log or business name" allowClear style={{ width: 220 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Apply
              </Button>
              <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
                Refresh
              </Button>
              <Button icon={<DownloadOutlined />} onClick={exportCsv}>
                Export CSV
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
      <Table<LogRow>
        className="retain-iq-reports-table"
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={data?.logs || []}
        scroll={{ x: 1200 }}
        tableLayout="fixed"
        onRow={(record) => ({
          onClick: () => setSelectedLog(record),
        })}
        rowClassName={(record) =>
          selectedLog?.id === record.id
            ? 'retain-iq-reports-row retain-iq-reports-row--active'
            : 'retain-iq-reports-row'
        }
        pagination={{
          current: data?.page || 1,
          pageSize: data?.limit || 50,
          total: data?.total || 0,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100', '200'],
          onChange: (page, pageSize) => {
            setParams((prev) => ({ ...prev, page: String(page), limit: String(pageSize || 50) }));
          },
        }}
      />

      <Drawer
        title={selectedLog ? `Call log — ${selectedLog.business_name || 'Business'}` : 'Call log'}
        width={640}
        open={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        destroyOnClose
      >
        {selectedLog && (
          <RetainIQCallLogDetail
            log={selectedLog}
            ticketLink={(n) => (
              <Button type="link" style={{ padding: 0, fontFamily: 'monospace' }} onClick={() => openTicket(n)}>
                #{n}
              </Button>
            )}
          />
        )}
      </Drawer>
    </div>
  );
};
